import { ApiAuthError } from '../errors/ApiError.js';

export const CONFIG_PATH = '/api/v1/config';

// Centralized authentication (Milestone 7.3, brief §3).
//
// Owns the entire token lifecycle so no other class ever holds a credential:
// PlayBidApiClient asks for a header, never for the token itself.
//
// The API key and access token are private fields and are never logged, never
// placed in error context, and never returned by any public getter. The one
// public window into auth state is `status`, which reports whether a token
// exists - not what it is.
export class AuthManager {
  #config;
  #transport;
  #accessToken = null;
  #tokenType = 'Bearer';
  #expiresAt = 0;
  #serverConfig = null;
  // A single in-flight promise, so N concurrent requests arriving with an
  // expired token trigger exactly one token exchange rather than N.
  #inFlight = null;

  constructor({ config, services: { transport } = {} }) {
    this.#config = config;
    this.#transport = transport;
  }

  get status() {
    return Object.freeze({
      authenticated: this.#accessToken !== null && !this.#isExpired(),
      // Milliseconds-since-epoch, safe to show in a UI; the token is not.
      expiresAt: this.#accessToken ? this.#expiresAt : null,
    });
  }

  // Explicit reset, used on 401 and on teardown.
  clear() {
    this.#accessToken = null;
    this.#expiresAt = 0;
    this.#inFlight = null;
  }

  async authenticate() {
    if (!this.#config.auth.enabled) return null;

    if (this.#accessToken && !this.#isExpired()) {
      return this.#accessToken;
    }

    this.#inFlight ??= this.#requestToken().finally(() => {
      this.#inFlight = null;
    });

    return this.#inFlight;
  }

  // Returns the header object rather than the token, so callers physically
  // cannot log or forward a credential they never received.
  async authorizationHeader() {
    const token = await this.authenticate();
    return token ? { Authorization: `${this.#tokenType} ${token}` } : {};
  }

  // The bootstrap levers the backend sent with the token (flush interval, batch
  // ceiling, heartbeat cadence). Never includes the token itself.
  get serverConfig() {
    return this.#serverConfig;
  }

  // GET /api/v1/config mints the short-lived bearer. The site key is public (it
  // ships in the page); the backend binds it to the site's registered origin,
  // so the key alone is not a credential worth guarding - the bearer is.
  /**
   * Adopt a `/api/v1/config` response fetched by someone else - boot() already
   * makes that call (B2), so the client starts authenticated instead of asking
   * a second time. Returns false, changing nothing, for a response without a
   * usable bearer.
   */
  seed(data) {
    try {
      this.#apply(data ?? {});
      return true;
    } catch {
      return false;
    }
  }

  #apply(data) {
    const { bearerToken, bearerTokenExpiresAt } = data;

    if (typeof bearerToken !== 'string' || bearerToken.length === 0) {
      throw new ApiAuthError('Config response did not contain a bearerToken', {
        endpoint: CONFIG_PATH,
        recoverable: false,
        context: { endpoint: CONFIG_PATH },
      });
    }

    this.#accessToken = bearerToken;
    this.#tokenType = 'Bearer';
    this.#expiresAt = Number.isFinite(bearerTokenExpiresAt) ? bearerTokenExpiresAt : 0;
    this.#serverConfig = Object.freeze({
      publisherId: typeof data.publisherId === 'string' ? data.publisherId : null,
      flushIntervalMs: data.flushIntervalMs ?? null,
      batchMaxEvents: data.batchMaxEvents ?? null,
      heartbeatIntervalMs: data.heartbeatIntervalMs ?? null,
      samplingRate: typeof data.samplingRate === 'number' ? data.samplingRate : 1,
    });
  }

  async #requestToken() {
    if (!this.#config.apiKey) {
      throw new ApiAuthError('Cannot authenticate: no apiKey configured', {
        endpoint: CONFIG_PATH,
        recoverable: false,
      });
    }

    const response = await this.#transport.request({
      method: 'GET',
      path: CONFIG_PATH,
      headers: { 'X-Publisher-Key': this.#config.apiKey },
    });

    this.#apply(response.data ?? {});
    return this.#accessToken;
  }

  #isExpired() {
    // Refreshed early by the configured skew so a token cannot expire in
    // flight between the check here and the server receiving the request.
    return Date.now() >= this.#expiresAt - this.#config.auth.refreshSkewMs;
  }
}
